"use client";

import { useEffect, useMemo, useState } from "react";
import { Plug, Wrench } from "lucide-react";
import BaseDialog from "@/components/Modals/BaseDialog";
import { Button } from "@/components/shadcn/button";
import { MCPToolRow } from "./MCPToolRow";
import type { MCPToolInfo } from "./mcpCredentials";

export interface MCPServerToolsDialogCredential {
  display_name: string;
  integration_name: string;
  integration_image: string;
  discovered_tools: MCPToolInfo[];
  enabled_tools: string[];
}

export function MCPServerToolsDialog({
  isOpen,
  onClose,
  credential,
  selectedTools,
  onSave,
  isDisabled,
}: {
  isOpen: boolean;
  onClose: () => void;
  credential: MCPServerToolsDialogCredential | undefined;
  selectedTools: string[] | null;
  onSave: (selected_tools: string[] | null) => void;
  isDisabled?: boolean;
}) {
  const connectionEnabled = useMemo(
    () => new Set(credential?.enabled_tools ?? []),
    [credential?.enabled_tools]
  );

  const availableTools = useMemo(
    () =>
      (credential?.discovered_tools ?? []).filter((t) =>
        connectionEnabled.has(t.name)
      ),
    [credential?.discovered_tools, connectionEnabled]
  );

  const initialSelected = useMemo(() => {
    if (selectedTools === null) {
      return new Set(availableTools.map((t) => t.name));
    }
    return new Set(selectedTools.filter((n) => connectionEnabled.has(n)));
  }, [selectedTools, availableTools, connectionEnabled]);

  const [draftSelected, setDraftSelected] = useState<Set<string>>(
    () => new Set(initialSelected)
  );

  useEffect(() => {
    if (isOpen) {
      setDraftSelected(new Set(initialSelected));
    }
  }, [isOpen, initialSelected]);

  const toggleTool = (name: string) => {
    if (isDisabled) return;
    setDraftSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const selectAll = () => {
    if (isDisabled) return;
    setDraftSelected(new Set(availableTools.map((t) => t.name)));
  };

  const selectNone = () => {
    if (isDisabled) return;
    setDraftSelected(new Set());
  };

  const handleSave = () => {
    const nextList = availableTools
      .map((t) => t.name)
      .filter((n) => draftSelected.has(n));
    const isAllSelected =
      nextList.length === availableTools.length && availableTools.length > 0;
    onSave(isAllSelected ? null : nextList);
    onClose();
  };

  const title = credential ? (
    <div className="flex items-center gap-2">
      {credential.integration_image ? (
        <img
          src={credential.integration_image}
          alt={credential.integration_name}
          className="size-6 rounded-sm shrink-0"
        />
      ) : (
        <Plug className="size-5 shrink-0 text-muted-foreground" />
      )}
      <span className="truncate">{credential.display_name}</span>
    </div>
  ) : (
    "MCP Server Tools"
  );

  return (
    <BaseDialog
      title={title}
      description="Choose which tools this agent can use from this MCP server."
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      size="2xl"
      disableScroll
      overflowVisible
      footerActions={[
        {
          label: "Cancel",
          props: { variant: "ghost", onClick: onClose },
        },
        {
          label: "Save",
          props: {
            onClick: handleSave,
            disabled: isDisabled,
          },
        },
      ]}
    >
      {availableTools.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
          <Wrench className="size-5" />
          <span>
            No tools available. Enable tools on this connection in the
            Integrations page.
          </span>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between px-1">
            <span className="text-xs text-muted-foreground">
              {draftSelected.size}/{availableTools.length} selected
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="xs"
                onClick={selectAll}
                disabled={isDisabled}
              >
                Select all
              </Button>
              <Button
                variant="outline"
                size="xs"
                onClick={selectNone}
                disabled={isDisabled}
              >
                Unselect all
              </Button>
            </div>
          </div>
          <div
            className="flex flex-col gap-1 max-h-[340px] -mr-4 overflow-y-auto overflow-x-hidden"
            style={{ scrollbarGutter: "stable" }}
          >
            {availableTools.map((tool) => (
              <MCPToolRow
                key={tool.name}
                tool={tool}
                integrationImage={credential?.integration_image}
                integrationName={credential?.integration_name}
                checked={draftSelected.has(tool.name)}
                onToggle={() => toggleTool(tool.name)}
                disabled={isDisabled}
              />
            ))}
          </div>
        </div>
      )}
    </BaseDialog>
  );
}
